import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useSpring, AnimatePresence } from "framer-motion";
import { Play, Pause } from "lucide-react";
import { useLang } from "../lib/lang";

const TILES = [
  { v: "medusa", t: { es: "Medusa neón", en: "Neon Medusa" }, tag: "3D · Motion", span: "lg:col-span-5", h: "lg:h-[560px]" },
  { v: "fluid", t: { es: "Silueta líquida", en: "Liquid silhouette" }, tag: "Motion loop", span: "lg:col-span-7", h: "lg:h-[560px]" },
  { v: "skater", t: { es: "Trazos de luz", en: "Light trails" }, tag: "Reel · Tracking", span: "lg:col-span-3", h: "lg:h-[600px]" },
  { v: "blobs", t: { es: "Burbujas cromadas", en: "Chrome bubbles" }, tag: "3D loop", span: "lg:col-span-6", h: "lg:h-[600px]" },
  { v: "paramo", t: { es: "Páramo × Domestika", en: "Páramo × Domestika" }, tag: "Course trailer", span: "lg:col-span-3", h: "lg:h-[600px]" },
  { v: "kaleido", t: { es: "Caleidoscopio", en: "Kaleidoscope" }, tag: "Pattern loop", span: "lg:col-span-7", h: "lg:h-[520px]" },
  { v: "medusa_glasses", t: { es: "Medusa nocturna", en: "Medusa after dark" }, tag: "3D · Motion", span: "lg:col-span-5", h: "lg:h-[520px]" },
  { v: "trevi", t: { es: "Roma de noche", en: "Rome by night" }, tag: "Reels", span: "lg:col-span-4", h: "lg:h-[480px]" },
  { v: "barcelona", t: { es: "Barcelona desde arriba", en: "Barcelona from above" }, tag: "Reels", span: "lg:col-span-4", h: "lg:h-[480px]" },
  { v: "pattern", t: { es: "Patrón psicodélico", en: "Psychedelic pattern" }, tag: "Motion loop", span: "lg:col-span-4", h: "lg:h-[480px]" },
];

const AutoVideo = ({ name, paused }) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.25 });
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  useEffect(() => {
    const v = ref.current;
    if (inView && !paused) v.play().catch(() => {});
    else v.pause();
  }, [inView, paused]);
  return <video ref={ref} src={`/video/${name}.mp4`} poster={`/video/${name}.jpg`} muted loop playsInline preload="metadata" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.05]" />;
};

const PinkCursor = ({ x, y, active, paused }) => (
  <motion.div style={{ x, y }} className="pointer-events-none fixed left-0 top-0 z-[70] hidden md:block" data-testid="motion-lab-cursor">
    <motion.div
      animate={{ width: active ? 96 : 14, height: active ? 96 : 14 }}
      transition={{ type: "spring", stiffness: 300, damping: 24 }}
      className="-translate-x-1/2 -translate-y-1/2 grid place-items-center rounded-full bg-[var(--acid)] text-[var(--ink)] shadow-[0_10px_40px_rgba(242,138,192,0.45)]"
    >
      <AnimatePresence>
        {active && (
          <motion.span initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.5 }} className="flex flex-col items-center gap-1 font-mono text-[10px] font-medium uppercase tracking-widest">
            {paused ? <Play size={22} fill="currentColor" /> : <Pause size={22} fill="currentColor" />}
            {paused ? "Play" : "Pause"}
          </motion.span>
        )}
      </AnimatePresence>
    </motion.div>
  </motion.div>
);

export const MotionLab = () => {
  const { tr } = useLang();
  const mx = useMotionValue(-200);
  const my = useMotionValue(-200);
  const x = useSpring(mx, { stiffness: 500, damping: 40 });
  const y = useSpring(my, { stiffness: 500, damping: 40 });
  const [inside, setInside] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [paused, setPaused] = useState({});
  const toggle = (v) => setPaused((p) => ({ ...p, [v]: !p[v] }));

  return (
    <section
      id="motion"
      data-testid="motion-lab-section"
      onMouseMove={(e) => { mx.set(e.clientX); my.set(e.clientY); }}
      onMouseEnter={() => setInside(true)}
      onMouseLeave={() => { setInside(false); setHovered(null); }}
      className="relative bg-[var(--greige)] px-4 py-24 text-[var(--ink)] sm:px-6 md:cursor-none lg:px-8 lg:py-32"
    >
      {inside && <PinkCursor x={x} y={y} active={!!hovered} paused={!!paused[hovered]} />}
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-col justify-between gap-6 border-b border-black/15 pb-10 lg:flex-row lg:items-end">
          <h2 className="font-display text-6xl font-medium leading-[0.9] tracking-tight sm:text-8xl">
            Motion <span className="font-serif-i">Lab</span>
          </h2>
          <p className="max-w-md text-base text-neutral-700 md:text-lg">
            {tr({ es: "Experimentos, reels y loops en movimiento: donde el diseño deja de estar quieto.", en: "Experiments, reels and loops in motion: where design stops standing still." })}
          </p>
        </div>
        <div className="mt-10 grid grid-cols-1 gap-4 lg:grid-cols-12">
          {TILES.map((t, i) => (
            <motion.figure
              key={t.v}
              data-testid={`motion-tile-${t.v}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 0.9, delay: (i % 3) * 0.08 }}
              className={`group ${t.span}`}
            >
              <button
                type="button"
                data-testid={`motion-tile-toggle-${t.v}`}
                aria-label={paused[t.v] ? "Play" : "Pause"}
                onClick={() => toggle(t.v)}
                onMouseEnter={() => setHovered(t.v)}
                onMouseLeave={() => setHovered(null)}
                className={`relative block aspect-[4/5] w-full overflow-hidden rounded-2xl bg-black sm:aspect-[16/10] md:cursor-none lg:aspect-auto ${t.h}`}
              >
                <AutoVideo name={t.v} paused={!!paused[t.v]} />
              </button>
              <figcaption className="mt-3 flex items-baseline justify-between gap-4">
                <span className="font-display text-xl font-medium sm:text-2xl">{tr(t.t)}</span>
                <span className="rounded-full bg-[var(--acid)] px-3 py-1 font-mono text-[11px] uppercase tracking-widest text-[var(--ink)]">{t.tag}</span>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
};
